<template>
    <div>
        <div class="md:flex">
            <div
                v-if="category.display_filters || category.tags.length > 0 || category.attributes.length > 0"
                class="md:w-1/5 md:pr-4"
            >
                <div class="mb-8">
                    <div class="-mx-4 md:mx-0 grid grid-cols-2 md:hidden bg-pink-100 divide-x divide-grey font-heading text-lg">
                        <div
                            class="flex items-center justify-center gap-2 py-2"
                            @click="showFilteringDrawer = true"
                        >
                            <FilterIcon class="w-4 h-4 text-grey-darkest" />
                            <span>
                                Filter
                            </span>
                        </div>
                        <div
                            class="flex items-center justify-center gap-2 py-2"
                            @click="showSortingDrawer = true"
                        >
                            <span>
                                Sort
                            </span>
                            <ChevronDownIcon class="w-4 h-4 text-grey-darkest" />
                        </div>
                    </div>

                    <Drawer
                        :open="showFilteringDrawer"
                        title="Filter Results"
                        position="left"
                        @close="showFilteringDrawer = false"
                    >
                        <CategoryFilters
                            :category="category"
                            :filter-by="filterBy"
                            :tag-ids="tagIds"
                            :attribute-ids="attributeIds"
                            @change:filter-by="filterBy = $event"
                            @change:tag-ids="tagIds = $event"
                            @change:attribute-ids="attributeIds = $event"
                        />
                    </Drawer>

                    <Drawer
                        :open="showSortingDrawer"
                        title="Sort Results"
                        @close="showSortingDrawer = false"
                    >
                        <CategorySorting
                            :sort-by="sortBy"
                            :sort-direction="sortDirection"
                            class="pb-2"
                            @change:sort-by="sortBy = $event"
                            @change:sort-direction="sortDirection = $event"
                        />
                    </Drawer>

                    <div class="hidden md:flex flex-col gap-6">
                        <CategorySorting
                            :sort-by="sortBy"
                            :sort-direction="sortDirection"
                            class="pb-2 md:pt-0"
                            @change:sort-by="sortBy = $event"
                            @change:sort-direction="sortDirection = $event"
                        />
                        <CategoryFilters
                            :category="category"
                            :filter-by="filterBy"
                            :tag-ids="tagIds"
                            :attribute-ids="attributeIds"
                            @change:filter-by="filterBy = $event"
                            @change:tag-ids="tagIds = $event"
                            @change:attribute-ids="attributeIds = $event"
                        />
                    </div>

                </div>
            </div>

            <div class="md:w-full">
                <div
                    v-if="category.display_number_filter"
                    class="mb-8 md:flex md:space-x-5 items-center"
                >
                    <div>
                        <h2>Which Age Would You Like?</h2>
                        <p class="mb-3">
                            Enter a number and we'll only show you balloons that are in stock.
                        </p>
                    </div>
                    <TextInput
                        v-model="filterNumber"
                        placeholder="e.g. 18"
                    />
                </div>

                <div
                    v-show="isLoadingProducts"
                    class="flex -mx-4"
                >
                    <CatalogueTilePlaceholder class="w-1/2 lg:w-1/3 mx-4" />
                    <CatalogueTilePlaceholder class="w-1/2 lg:w-1/3 mx-4" />
                    <CatalogueTilePlaceholder class="hidden lg:block w-1/2 lg:w-1/3 mx-4" />
                </div>

                <div v-show="!isLoadingProducts">

                    <div
                        v-show="areProducts"
                        class="grid grid-cols-2 lg:grid-cols-3 gap-4 gap-y-8 lg:gap-8 mb-8"
                    >
                        <div
                            v-for="(product, index) in products"
                            :key="index"
                        >
                            <ProductCard
                                :product="product"
                                :position="index"
                                list="Category"
                                :category="category.name"
                                :available-for-delivery="categoryAvailableForDelivery"
                                class=""
                            />
                        </div>
                    </div>

                    <div
                        v-show="isLoadMoreProductsLoading"
                        class="grid grid-cols-2 lg:grid-cols-3 gap-4 gap-y-8 lg:gap-8 mb-8"
                    >
                        <CatalogueTilePlaceholder />
                        <CatalogueTilePlaceholder />
                        <CatalogueTilePlaceholder class="hidden lg:block" />
                    </div>

                    <div
                        v-if="isLoadMoreButtonShown"
                        class="flex flex-col items-center gap-4"
                    >
                        <p v-if="loadFailed">
                            We couldn't load more products.
                        </p>
                        <Button
                            :text="loadFailed ? 'Try Again' : 'Load More Products'"
                            bg="2025-dark-pink"
                            @click="loadMoreProducts"
                        />
                    </div>

                    <NoSearchResultsMessage v-show="!areProducts && !loadFailed && !isLoadMoreProductsLoading" />

                </div>

                <div
                    ref="scrollSentinel"
                    aria-hidden="true"
                />
            </div>
        </div>

    </div>
</template>

<script>

// import CategorySorting from '~/components/CategorySorting';
import debounce from 'debounce';
import TextInput from '@/components/Input';
import CategoryFiltering from '~/components/CategoryFiltering';
import CategorySearch from '~/components/CategorySearch';
import CategorySorting from '@/components/CategorySorting';
import CategoryTagSelect from '~/components/CategoryTagSelect';
import CategoryAttributeSelect from '~/components/CategoryAttributeSelect';
import ProductCard from '~/components/ProductCard';
import CatalogueTilePlaceholder from '~/components/CatalogueTilePlaceholder';
import NoSearchResultsMessage from '~/components/NoSearchResultsMessage';
import Collapse from '~/components/Collapse';
import Button from '~/components/Button';
import CategoryFilters from '~/components/CategoryFilters';
import Drawer from '~/components/Drawer';
import ChevronDownIcon from '~/static/fonts/LineIcons/chevron-down.svg';
import FilterIcon from '~/static/fonts/LineIcons/filter.svg';
/* eslint-disable-next-line */
import tailwindConfig from '@/tailwind.config.js';

const PAGE_LENGTH = 24;
const PAGES_PER_BATCH = 3;
const SCROLL_POSITION_KEY = 'category-products-scroll';
const SORT_OPTIONS = ['position', 'recommended', 'popularity', 'price', 'created_at'];
const SORT_DIRECTIONS = ['asc', 'desc'];
const FILTER_OPTIONS = ['all', 'personalised', 'non-personalised'];
// The query parameters this component owns. Others, such as utm_source, stay in the URL.
const QUERY_KEYS = ['page', 'sort', 'direction', 'filter', 'number', 'tags', 'attributes'];

const parseIds = (value) => String(value || '')
    .split(',')
    .map((id) => parseInt(id, 10))
    .filter((id) => id > 0);

const parsePage = (value) => Math.max(parseInt(value, 10) || 1, 1);

const defaultFilters = (category) => ({
    sortBy: category.default_sort_by || 'position',
    sortDirection: category.default_sort_direction || 'desc',
    filterBy: 'all',
    filterNumber: '',
    tagIds: [],
    attributeIds: [],
});

const filtersFromQuery = (query, category) => {
    const defaults = defaultFilters(category);

    return {
        sortBy: SORT_OPTIONS.includes(query.sort) ? query.sort : defaults.sortBy,
        sortDirection: SORT_DIRECTIONS.includes(query.direction) ? query.direction : defaults.sortDirection,
        filterBy: FILTER_OPTIONS.includes(query.filter) ? query.filter : defaults.filterBy,
        filterNumber: query.number ? String(query.number) : defaults.filterNumber,
        tagIds: parseIds(query.tags),
        attributeIds: parseIds(query.attributes),
    };
};

const filterSignature = (filters) => JSON.stringify({
    sortBy: filters.sortBy,
    sortDirection: filters.sortDirection,
    filterBy: filters.filterBy,
    filterNumber: filters.filterNumber,
    tagIds: [...filters.tagIds].sort((a, b) => a - b),
    attributeIds: [...filters.attributeIds].sort((a, b) => a - b),
});

export default {

    components: {
        CategorySorting,
        CategoryFiltering,
        CategorySearch,
        CategoryTagSelect,
        CategoryAttributeSelect,
        ProductCard,
        CatalogueTilePlaceholder,
        Button,
        NoSearchResultsMessage,
        Collapse,
        TextInput,
        CategoryFilters,
        Drawer,
        ChevronDownIcon,
        FilterIcon,
    },

    props: {
        category: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            products: [],
            isLoadingProducts: true,
            // The last page loaded. Products fill pages 1 to this with no gaps.
            pageNumber: 0,
            hasMoreProducts: false,
            isLoadMoreProductsLoading: false,
            loadFailed: false,
            supportsInfiniteScroll: true,
            ...filtersFromQuery(this.$route.query, this.category),
            searchQuery: '',
            openCollapseIndex: null,
            showFilteringDrawer: false,
            showSortingDrawer: false,
        };
    },

    computed: {
        areFiltersApplied() {
            return this.sortBy !== 'position'
                || this.sortDirection !== 'asc'
                || this.filterBy !== 'all'
                || this.filterNumber
                || this.searchQuery !== ''
                || this.tagIds.length > 0
                || this.attributeIds.length > 0;
        },
        areProducts() {
            return !!this.products.length;
        },
        isMobile() {
            return typeof window !== 'undefined' && window.innerWidth < parseInt(tailwindConfig.theme.screens.md, 10);
        },
        collapseComponent() {
            return this.isMobile ? Collapse : 'div';
        },
        categoryAvailableForDelivery() {
            if (!this.category.check_delivery_postcode) {
                return true;
            }

            return this.$store.getters['checkout/checkProductAvailable'](this.category.shipping_class_id);
        },
        currentFilterSignature() {
            return filterSignature(this);
        },
        isLoadMoreButtonShown() {
            return this.hasMoreProducts
                && !this.isLoadingProducts
                && !this.isLoadMoreProductsLoading
                && (this.loadFailed || !this.supportsInfiniteScroll);
        },
    },

    watch: {
        currentFilterSignature(signature) {
            if (signature === filterSignature(filtersFromQuery(this.$route.query, this.category))) {
                this.navigateToFilters.clear();
                return;
            }

            this.navigateToFilters();
        },
        // The URL decides what is listed, so back, forward and links all arrive here.
        $route(to) {
            if (to.path !== this.listingPath) {
                return;
            }

            this.navigateToFilters.clear();
            Object.assign(this, filtersFromQuery(to.query, this.category));

            const page = parsePage(to.query.page);

            // Each list belongs to one history entry. A different entry means a
            // link to the category, or back or forward to another list.
            if (this.currentFilterSignature !== this.loadedSignature || this.currentHistoryKey() !== this.historyKey) {
                this.saveListing();
                this.pendingScrollY = null;
                this.historyKey = this.currentHistoryKey();
                this.showListing(page);
            } else if (page > this.pageNumber) {
                this.loadThroughPage(page);
            }
        },
    },

    created() {
        this.listingPath = this.$route.path;
        this.navigateToFilters = debounce(this.pushFiltersToUrl, 500);
        this.requestSource = null;
        this.loadedSignature = null;
        this.targetPage = 1;

        // On the server the products are left for the browser to load. Going
        // back to this page has to render them now, so the router can scroll to
        // the saved position.
        if (process.client) {
            this.historyKey = this.currentHistoryKey();
            this.restoredListing = this.restoreListing();
        }
    },

    mounted() {
        const page = this.pageInUrl();

        this.pendingScrollY = this.takeSavedScrollPosition();
        window.addEventListener('pagehide', this.saveScrollPosition);

        if (!this.loadedSignature) {
            this.observeScrollSentinel();
            this.showListing(page);
            return;
        }

        // A remount after signing in keeps the route, so the router does not
        // scroll. The sentinel waits for the position, or it would see the end of
        // the list from wherever the page happens to be and load more.
        this.$nextTick(() => {
            window.scrollTo(0, this.restoredListing.scrollY);
            this.observeScrollSentinel();

            if (page > this.pageNumber) {
                this.loadThroughPage(page);
            }
        });
    },

    beforeDestroy() {
        this.navigateToFilters.clear();
        window.removeEventListener('pagehide', this.saveScrollPosition);

        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
        }

        if (this.requestSource) {
            this.requestSource.cancel();
            this.requestSource = null;
        }

        this.saveListing();
    },

    methods: {
        listingKey(signature = this.currentFilterSignature) {
            return `${this.category.id}:${signature}:${this.historyKey}`;
        },
        saveListing() {
            if (!this.loadedSignature || this.pageNumber < 1) {
                return;
            }

            this.$store.commit('categoryListings/SET_LISTING', {
                key: this.listingKey(this.loadedSignature),
                products: this.products,
                pageNumber: this.pageNumber,
                hasMoreProducts: this.hasMoreProducts,
                scrollY: window.scrollY,
            });
        },
        restoreListing() {
            const listing = this.$store.getters['categoryListings/listing'](this.listingKey());

            if (!listing) {
                return null;
            }

            this.products = [...listing.products];
            this.pageNumber = listing.pageNumber;
            this.hasMoreProducts = listing.hasMoreProducts;
            this.isLoadingProducts = false;
            this.isLoadMoreProductsLoading = false;
            this.loadFailed = false;
            this.loadedSignature = this.currentFilterSignature;
            this.targetPage = listing.pageNumber;

            return listing;
        },
        // Scrolling updates the address bar without telling the router, so
        // $route can hold an older page number.
        pageInUrl() {
            return parsePage(new URLSearchParams(window.location.search).get('page'));
        },
        // The router's key for this history entry. Lists are stored against it,
        // so back and forward find their own list and a new link starts afresh.
        currentHistoryKey() {
            return window.history.state ? window.history.state.key : null;
        },
        // Going back to a list too old to reuse loads it again, then returns to
        // where the shopper was.
        restoreExpiredScrollPosition() {
            const listing = this.$store.getters['categoryListings/storedListing'](this.listingKey());

            if (this.pendingScrollY === null && listing) {
                this.pendingScrollY = listing.scrollY;
            }
        },
        // False while a filter change waits for its debounce, and while the
        // page transition runs after leaving the category.
        isShowingLoadedListing() {
            return this.$route.path === this.listingPath
                && this.currentFilterSignature === this.loadedSignature;
        },
        // Shows the list for the current filters, from the store if it is there.
        showListing(page) {
            if (this.requestSource) {
                this.requestSource.cancel();
                this.requestSource = null;
            }

            const listing = this.restoreListing();

            if (listing) {
                if (page > this.pageNumber) {
                    this.loadThroughPage(page);
                    return;
                }

                // Query-only navigations keep the scroll position, so back or
                // forward to a list has to return to its place here.
                this.$nextTick(() => window.scrollTo(0, listing.scrollY));

                this.syncPageToUrl();
                this.recheckScrollSentinel();
                return;
            }

            this.products = [];
            this.pageNumber = 0;
            this.hasMoreProducts = true;
            this.isLoadingProducts = true;
            this.loadFailed = false;
            this.loadedSignature = this.currentFilterSignature;
            this.restoreExpiredScrollPosition();
            this.scrollToListTop();
            this.loadThroughPage(page);
        },
        // A new list starts from the top. Keeping a deep scroll position would
        // make the scroll sentinel load page after page to catch up with it.
        scrollToListTop() {
            const top = this.$el.getBoundingClientRect().top + window.scrollY;

            if (this.pendingScrollY === null && window.scrollY > top) {
                window.scrollTo(0, top);
            }
        },
        fetchProductsPage(page, cancelToken) {
            // The filters of the list being loaded, not any change still in its debounce.
            const filters = JSON.parse(this.loadedSignature);
            const params = {
                page_number: page,
                page_length: PAGE_LENGTH,
                sort_by: filters.sortBy,
                sort_direction: filters.sortDirection,
                filter_by: filters.filterBy,
                filter_number: filters.filterNumber,
            };

            if (filters.tagIds.length > 0) {
                params.tag_id = filters.tagIds;
            }

            if (filters.attributeIds.length > 0) {
                params.attribute_id = filters.attributeIds;
            }

            return this.$axios.$get(`/categories/${this.category.id}/products`, {
                params,
                cancelToken,
                // A timed-out page is expensive, and the shopper can try again.
                'axios-retry': { retries: 0 },
                'paramsSerializer': (requestParams) => {
                    const searchParams = new URLSearchParams();
                    Object.keys(requestParams).forEach((key) => {
                        const value = requestParams[key];
                        if (Array.isArray(value)) {
                            value.forEach((item) => {
                                searchParams.append(`${key}[]`, item);
                            });
                        } else if (value !== null && value !== undefined) {
                            searchParams.append(key, value);
                        }
                    });
                    return searchParams.toString();
                },
            });
        },
        // Loads the pages after the last one loaded, a few at a time, and adds
        // them in order. It stops at the end of the category or at a failure.
        async loadThroughPage(page) {
            if (this.requestSource) {
                this.targetPage = Math.max(this.targetPage, page);
                return;
            }

            if (!this.isShowingLoadedListing()) {
                return;
            }

            const source = this.$axios.CancelToken.source();
            this.requestSource = source;
            this.targetPage = page;
            this.loadFailed = false;
            this.isLoadMoreProductsLoading = !this.isLoadingProducts;

            while (this.hasMoreProducts && this.pageNumber < this.targetPage && !this.loadFailed) {
                const firstPage = this.pageNumber + 1;
                const count = Math.min(PAGES_PER_BATCH, this.targetPage - this.pageNumber);
                const pages = Array.from({ length: count }, (_, index) => firstPage + index);

                // Each page settles on its own, so a failure keeps the pages before it.
                // eslint-disable-next-line no-await-in-loop
                const results = await Promise.all(pages.map((batchPage) => (
                    this.fetchProductsPage(batchPage, source.token)
                        .then((products) => ({ products }))
                        .catch((error) => ({ error }))
                )));

                if (this.requestSource !== source) {
                    return;
                }

                for (let index = 0; index < results.length && this.hasMoreProducts; index += 1) {
                    const { products, error } = results[index];

                    if (error) {
                        this.loadFailed = true;
                        break;
                    }

                    this.products.push(...products);
                    this.pageNumber = pages[index];
                    this.hasMoreProducts = products.length >= PAGE_LENGTH;
                }

                this.isLoadingProducts = false;
                this.isLoadMoreProductsLoading = this.hasMoreProducts
                    && this.pageNumber < this.targetPage
                    && !this.loadFailed;
                this.syncPageToUrl();
            }

            this.requestSource = null;
            this.isLoadingProducts = false;
            this.isLoadMoreProductsLoading = false;
            this.restorePendingScrollPosition();
            this.recheckScrollSentinel();
        },
        loadMoreProducts() {
            this.loadThroughPage(Math.max(this.targetPage, this.pageNumber + 1));
        },
        queryForFilters(page, filters = this) {
            const query = { ...this.$route.query };
            QUERY_KEYS.forEach((key) => delete query[key]);

            const defaults = defaultFilters(this.category);

            if (page > 1) {
                query.page = String(page);
            }

            if (filters.sortBy !== defaults.sortBy) {
                query.sort = filters.sortBy;
            }

            if (filters.sortDirection !== defaults.sortDirection) {
                query.direction = filters.sortDirection;
            }

            if (filters.filterBy !== defaults.filterBy) {
                query.filter = filters.filterBy;
            }

            if (filters.filterNumber) {
                query.number = filters.filterNumber;
            }

            if (filters.tagIds.length > 0) {
                query.tags = filters.tagIds.join(',');
            }

            if (filters.attributeIds.length > 0) {
                query.attributes = filters.attributeIds.join(',');
            }

            return query;
        },
        // A filter change gets its own history entry, so back undoes it.
        pushFiltersToUrl() {
            this.$router.push({ path: this.listingPath, query: this.queryForFilters(1) }).catch(() => {});
        },
        // Scrolling only keeps the page number current. It adds no history entry,
        // so back leaves the category rather than stepping through its pages,
        // and it skips the router, which has nothing to do for it.
        syncPageToUrl() {
            if (this.pageNumber < 1 || this.$route.path !== this.listingPath) {
                return;
            }

            const { href, route } = this.$router.resolve({
                path: this.listingPath,
                query: this.queryForFilters(this.pageNumber, JSON.parse(this.loadedSignature)),
            });

            if (`${window.location.pathname}${window.location.search}` !== href) {
                window.history.replaceState(window.history.state, '', href);
                // vue-router has no public way to do this. Without it, the router
                // treats a link to the category as a duplicate navigation and
                // puts its older URL back in the address bar.
                this.$router.history.current = route;
            }
        },
        observeScrollSentinel() {
            if (!('IntersectionObserver' in window)) {
                this.supportsInfiniteScroll = false;
                return;
            }

            // Starts loading before the shopper reaches the end of the list.
            this.scrollObserver = new IntersectionObserver((entries) => {
                const isNearEnd = entries.some((entry) => entry.isIntersecting);

                if (isNearEnd
                    && this.hasMoreProducts
                    && !this.loadFailed
                    && !this.requestSource
                    && this.isShowingLoadedListing()) {
                    this.loadMoreProducts();
                }
            }, { rootMargin: '0px 0px 800px 0px' });

            this.scrollObserver.observe(this.$refs.scrollSentinel);
        },
        // The observer only reports changes. A short list can leave the sentinel
        // in view after a load, so observing again asks for its state afresh.
        recheckScrollSentinel() {
            if (!this.scrollObserver) {
                return;
            }

            this.$nextTick(() => {
                if (this.scrollObserver && this.$refs.scrollSentinel) {
                    this.scrollObserver.unobserve(this.$refs.scrollSentinel);
                    this.scrollObserver.observe(this.$refs.scrollSentinel);
                }
            });
        },
        saveScrollPosition() {
            try {
                window.sessionStorage.setItem(SCROLL_POSITION_KEY, JSON.stringify({
                    url: window.location.href,
                    y: window.scrollY,
                }));
            } catch (e) {
                // Without storage, a reload starts at the top of the list.
            }
        },
        // A reload, or a phone reopening a discarded tab, loads the list after
        // the browser has tried to restore the position, so it is restored here.
        takeSavedScrollPosition() {
            try {
                const saved = JSON.parse(window.sessionStorage.getItem(SCROLL_POSITION_KEY));
                window.sessionStorage.removeItem(SCROLL_POSITION_KEY);

                const [navigation] = window.performance.getEntriesByType('navigation');
                const isReturning = navigation && ['reload', 'back_forward'].includes(navigation.type);

                return isReturning && saved && saved.url === window.location.href ? saved.y : null;
            } catch (e) {
                return null;
            }
        },
        restorePendingScrollPosition() {
            if (this.pendingScrollY === null || this.$route.path !== this.listingPath) {
                return;
            }

            const y = this.pendingScrollY;
            this.pendingScrollY = null;
            this.$nextTick(() => window.scrollTo(0, y));
        },
        toggleCollapse() {
            if (this.openCollapseIndex === 1) {
                this.closeCollapse();
            } else {
                this.openCollapse();
            }
        },
        openCollapse() {
            this.openCollapseIndex = 1;
        },
        closeCollapse() {
            this.openCollapseIndex = null;
        },
    },

};
</script>

<style lang="scss" scoped>
</style>
